/** @jsxImportSource @emotion/react */
import React, { memo, useContext } from 'react'
import { ActiveAccountContext } from '@signal/core/user/Context'
import { UserAccountType } from '@signal/core/user/types'
import renderIf from '@signal/utils/renderIf'
import { AccountPermissionsFetcher, TenantPermissionsFetcher } from './Fetcher'

export type AccountPermissionsLoaderProps = {
  children: React.ReactNode
}

/**
 * @component AccountPermissionsLoader
 * The <UserRoot> React component, which fetches user data using the context fetcher
 *
 * IMPORTANT: Always renders the same tree structure (AccountPermissionsFetcher wrapping children)
 * to prevent React from unmounting/remounting the entire subtree when activeAccount changes.
 * The Fetcher handles null accountId gracefully by skipping the fetch.
 */
export function AccountPermissionsLoader({
  children
}: AccountPermissionsLoaderProps): JSX.Element {
  const { activeAccount } = useContext(ActiveAccountContext)

  // Always render the same tree structure to prevent unmount/remount cycles
  // When accountId is undefined, the fetcher will skip fetching account permissions but maintain tree structure
  return (
    <>
      <TenantPermissionsFetcher>
        {renderIf(
          Boolean(activeAccount),
          () => (
            <AccountPermissionsFetcher
              accountId={(activeAccount as UserAccountType)?.accountId}
            >
              {children}
            </AccountPermissionsFetcher>
          ),
          () => children
        )}
      </TenantPermissionsFetcher>
    </>
  )
}

export default memo(AccountPermissionsLoader)
